import { ITag } from '@/validations/tag';
import { Capacitor } from '@capacitor/core';
// ActionSheet.tsx
import { ActionSheetButton, IonActionSheet, IonContent, IonGrid, IonModal } from '@ionic/react';
import { closeOutline, cloudDownloadOutline, eyeOutline, person, pricetagOutline, trashOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';

import styles from '@/components/Actionsheets/ActionSheet.module.css';
import { DeleteReportModal } from '@/components/DeleteReportModal/DeleteReportModal';
import { PDFDownloadModalContent } from '@/components/PDFDownload/PDFDownloadModal';
import { TagModal } from '@/components/TagModal/TagModal';

import { isITagArray } from '@/utils/isITagArray';

import { useReportContext } from '@/contexts/reportContext';

import { ReportInfo } from '@/models';

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  reportInfo: ReportInfo;
  doDelete: (id: string) => void;
  showDeleteError: (error: boolean) => void;
  showPDFError: (error: boolean) => void;
  isError?: boolean;
  showAssignToSession?: boolean;
}

// ! This component is used on the reportListCard & on the SessionCard
export const ActionSheetReportCard: React.FC<ActionSheetProps> = ({
  isOpen,
  onClose,
  reportInfo,
  doDelete,
  showDeleteError,
  showPDFError,
  isError = false,
  showAssignToSession = false,
}) => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isAreYouSureModalOpen, setAreYouSureModalOpen] = useState(false);

  const handleDelete = () => {
    if (reportInfo.id) {
      doDelete(reportInfo.id);
    } else {
      showDeleteError(true);
    }
  };

  // #region Tag Management
  const { editTags, tags } = useReportContext();
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<ITag[]>(tags);
  const openTagModal = () => setTagModalOpen(true);
  const closeTagModal = () => setTagModalOpen(false);

  useEffect(() => {
    if (isITagArray(tags)) {
      setSelectedTags(tags);
    }
  }, [tags]);

  // Handle tag selection/creation from modal
  const handleTagModalClose = (changedTags?: ITag[]) => {
    closeTagModal();

    // If nothing is changed, return
    if (!changedTags) return;

    const updatedTags = changedTags ?? [];

    // Update local state to reflect the newly changed tags
    setSelectedTags(updatedTags);

    // Update the tags in your data store or server
    if (reportInfo.id) {
      editTags(
        reportInfo.id,
        updatedTags.map((t) => t.id),
      );
    }
  };

  const isNative = Capacitor.isNativePlatform();
  // #endregion Tag Management

  return (
    <>
      <IonActionSheet
        buttons={
          [
            {
              text: 'Add / change tags',
              icon: pricetagOutline,
              handler: () => {
                openTagModal();
              },
            },
            isNative && {
              text: 'Generate PDF (Only available on web)',
              icon: cloudDownloadOutline,
              disabled: true,
              cssClass: styles.disabledButton,
            },
            !isError &&
              !isNative && {
                text: 'Generate PDF',
                icon: cloudDownloadOutline,
                handler: () => {
                  setShowDownloadModal(true);
                },
              },
            {
              text: 'Delete',
              role: 'destructive',
              icon: trashOutline,
              handler: () => {
                setAreYouSureModalOpen(true);
              },
            },
            {
              text: 'Cancel',
              icon: closeOutline,
              role: 'cancel',
            },
          ].filter(Boolean) as ActionSheetButton[]
        }
        className={styles.actionSheet}
        isOpen={isOpen}
        onDidDismiss={onClose}
      />

      {/* Tag Modal */}
      <TagModal
        handleCancel={closeTagModal}
        handleClose={handleTagModalClose}
        initialValue={reportInfo.tags}
        isOpen={tagModalOpen}
      />

      {/* PDF Download Modal */}
      <IonModal className={styles.modal} isOpen={showDownloadModal} onDidDismiss={() => setShowDownloadModal(false)}>
        <IonContent className=''>
          <PDFDownloadModalContent
            PDFError={showPDFError}
            onClose={() => setShowDownloadModal(false)}
            reportInfo={reportInfo}
          />
        </IonContent>
      </IonModal>

      <DeleteReportModal
        handleClose={() => setAreYouSureModalOpen(false)}
        isOpen={isAreYouSureModalOpen}
        onConfirm={handleDelete}
      />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default ActionSheetReportCard;
